import { useMemo } from 'react'
import useSWR from 'swr'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { fetcher, staticOptions } from 'src/lib/core/api-client'
import { apiURI } from 'src/lib/utils/constants'
import { SegmentTemplateType } from 'src/lib/types/segment'

/**
 * Shared SWR fetch + normalization for segment templates (TemplatesView, TemplateDropdown).
 */
export function useSegmentTemplates(): {
  templates: SegmentTemplateType[]
  categories: string[]
  isLoading: boolean
  error: unknown
} {
  const {
    getters: { merchant }
  } = useMerchant()

  const {
    data: templatesResponse,
    error,
    isValidating: isLoading
  } = useSWR(
    merchant?.accountId
      ? [`/merchant/${merchant.accountId}/segments/templates`, apiURI.SEGMENTS_ENGINE]
      : null,
    ([url, baseURI]) => fetcher(url, baseURI),
    staticOptions
  )

  const templates: SegmentTemplateType[] = useMemo(() => {
    if (isLoading || error || !templatesResponse) {
      return []
    }
    const responseData = templatesResponse as { success?: boolean; data?: SegmentTemplateType[] }
    if (!responseData.data || !Array.isArray(responseData.data)) {
      return []
    }
    return responseData.data
  }, [templatesResponse, isLoading, error])

  const categories = useMemo(() => {
    const uniqueCategories = new Set(templates.map(t => t.category))
    return Array.from(uniqueCategories).sort()
  }, [templates])

  return { templates, categories, isLoading, error }
}
